import React, { useEffect, useMemo, useState, useCallback } from 'react'
import fetchHook from '../../../hooks/fetchMSSQL-hook'
import NoResults from '../../../components/NoResults/NoResults'
import moment from 'moment'
import SearchFilter from '../../../components/Filter/SearchFilter'
import renderPAPR from './components/PAPR'
import renderRD from './components/RD'
import renderRF from './components/RF'
import renderZE from './components/ZE'
import renderWN from './components/WN'
import { NotificationManager } from 'react-notifications'

export default function Reports() {
  const [fiscDocs, setFiscDocs] = useState([])
  const [fiscDocsCount, setFiscDocsCount] = useState(0)
  const [windowHeight, setWindowHeight] = useState(window.innerHeight)

  const [fiscalDocBtn, setFiscalDocBtn] = useState({ Report: null, Id: null })
  const [state, setState] = useState({ Report: null, Id: null })

  const { fetchMssqlApi } = fetchHook()

  const FILTER_NAME = 'wkfsReports'
  const clientId = sessionStorage.getItem('clientId') || 'noClient'
  const filterKey = `${clientId}_${FILTER_NAME}Filter`

  const defaultFilter = useMemo(
    () => ({
      name: 'cashRegistersReport',
      page: 1,
      rowsPerPage: 50,
      dateTimeRangeFrom: moment().startOf('day').format('YYYY-MM-DDTHH:mm:ss'),
      dateTimeRangeTo: moment().endOf("day").format("YYYY-MM-DDTHH:mm:ss"),
      cashRegisterId: null,
      nrUnikName: '',
      fiscalDocTypeId: null,
      fiscalDocTypeName: '',
    }),
    []
  )

  const [filter, setFilter] = useState(() => {
    try {
      const raw = localStorage.getItem(filterKey)
      if (raw) return { ...JSON.parse(raw), page: 1 }
    } catch {}
    return defaultFilter
  })

  const [settingsFilter, setSettingsFilter] = useState({
    saveFilters: true,
    autoLoading: true,
    additionalFilter: true,
    flatView: false,
    rowsPerPage: 50,
  })

  const toInt = (v) => {
    const n = Number(v)
    if (!Number.isFinite(n)) return null
    return Math.trunc(n)
  }

  const clampPage = (page, max) => {
    const p = toInt(page)
    if (!p || p < 1) return 1
    const m = Math.max(1, toInt(max) || 1)
    return Math.min(Math.max(p, 1), m)
  }

  const rowsPerPage = useMemo(() => {
    const s = toInt(settingsFilter.rowsPerPage)
    const f = toInt(filter.rowsPerPage)
    return s || f || 50
  }, [settingsFilter.rowsPerPage, filter.rowsPerPage])

  const maxPage = useMemo(() => {
    const total = Number(fiscDocsCount) || 0
    return Math.max(1, Math.ceil(total / rowsPerPage))
  }, [fiscDocsCount, rowsPerPage])

  useEffect(() => {
    setFilter((prev) => {
      const nextPage = clampPage(prev.page, maxPage)
      if (nextPage === prev.page) return prev
      return { ...prev, page: nextPage }
    })
  }, [maxPage])

  const normalizePaging = (page, size) => {
    const p = toInt(page)
    const s = toInt(size)
    return {
      PageNo: p && p > 0 ? p : 1,
      RecordsPerPage: s && s > 0 ? s : 50,
    }
  }

  const getFiscDocs = useCallback(
    (f = filter, pageSize, pageNo) => {
      const currentFilter = typeof f === 'boolean' ? filter : f

      if (
        !moment(currentFilter.dateTimeRangeFrom).isValid() ||
        !moment(currentFilter.dateTimeRangeTo).isValid()
      ) {
        NotificationManager.error('Wybierz poprawną datę')
        return
      }

      const paging = normalizePaging(pageNo ?? currentFilter.page, pageSize ?? rowsPerPage)

      const currentMaxPage = Math.max(
        1,
        Math.ceil((Number(fiscDocsCount) || 0) / (Number(paging.RecordsPerPage) || 50))
      )

      if (paging.PageNo > currentMaxPage) {
        setFilter((prev) => {
          const nextPage = clampPage(prev.page, currentMaxPage)
          return nextPage === prev.page ? prev : { ...prev, page: nextPage }
        })
        return
      }

      const from = moment(currentFilter.dateTimeRangeFrom).startOf('day').format('YYYY-MM-DD HH:mm:ss')
      const to = moment(currentFilter.dateTimeRangeTo).endOf('day').format('YYYY-MM-DD HH:mm:ss')

      const payload = {
        CashRegisterId: currentFilter.cashRegisterId ?? null,
        FiscalDocTypeId: currentFilter.fiscalDocTypeId ?? null,
        FromCreateDateTime: from,
        ToCreateDateTime: to,
        PageNo: paging.PageNo,
        RecordsPerPage: paging.RecordsPerPage,
      }

      fetchMssqlApi(
        'fiscalisationDocs',
        { method: 'POST', data: payload, hideNotification: true },
        (res) => {
          setFiscDocs(res?.Docs ?? [])
          setFiscDocsCount(res?.TotalRowCount ?? 0)
        }
      )
    },
    [filter, rowsPerPage, fiscDocsCount, fetchMssqlApi]
  )

  useEffect(() => {
    getFiscDocs(filter, rowsPerPage, filter.page)
  }, [])

  useEffect(() => {
    try {
      localStorage.setItem(filterKey, JSON.stringify(filter))
    } catch {}
  }, [filter, filterKey])

  useEffect(() => {
    const handleResize = () => setWindowHeight(window.innerHeight)
    window.addEventListener('resize', handleResize)
    handleResize()
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const renderElement = (obj, idx) => {
    switch (obj.FiscalDocTypeId) {
      case 1:
      case 2:
        return renderPAPR(obj, idx, fiscalDocBtn, setFiscalDocBtn, state, setState)
      case 3:
        return renderRD(obj, idx, fiscalDocBtn, setFiscalDocBtn, state, setState)
      case 4:
        return renderZE(obj, idx, fiscalDocBtn, setFiscalDocBtn, state, setState)
      case 5:
        return renderRF(obj, idx, fiscalDocBtn, setFiscalDocBtn, state, setState)
      case 7:
        return renderWN(obj, idx, fiscalDocBtn, setFiscalDocBtn)
      default:
        return <tr key={idx} />
    }
  }

  return (
    <>
      <SearchFilter
        filterName={FILTER_NAME}
        defaultFilter={defaultFilter}
        onFilterChange={(nextFilter) => {
          setFilter((prev) => {
            const incomingPage = toInt(nextFilter?.page)

            const total = Number(fiscDocsCount) || 0
            const size =
              toInt(settingsFilter.rowsPerPage) ||
              toInt(prev.rowsPerPage) ||
              50

            const maxPage = Math.max(1, Math.ceil(total / size))

            if (
              incomingPage != null &&
              incomingPage > prev.page &&
              prev.page >= maxPage
            ) {
              return prev
            }

            const nextPage =
              incomingPage != null
                ? clampPage(incomingPage, maxPage)
                : prev.page

            return {
              ...prev,
              ...nextFilter,
              page: nextPage,
            }
          })
        }}

        onSettingsChange={(s) => setSettingsFilter((prev) => ({ ...prev, ...s }))}
        search={getFiscDocs}
        totalCount={fiscDocsCount}
        dataList={fiscDocs}
        forNow
      />

      <div className="card">
        <div className="card-header pe-none">
          <h5>Dokumenty</h5>
        </div>

        <div className="overflow-auto custom-scroll" style={{ maxHeight: `${windowHeight - 268}px` }}>
          {fiscDocs.length ? (
            <table className="table table-striped">
              <thead>
                <tr className="headers-color pe-none">
                  <th>Typ dokumentu</th>
                  <th>Nr unikatowy / Nr ewidencyjny</th>
                  <th>Data / czas</th>
                  <th colSpan={2}>Dodatkowe informacje</th>
                  <th />
                </tr>
              </thead>
              <tbody>{fiscDocs.map(renderElement)}</tbody>
            </table>
          ) : (
            <NoResults />
          )}
        </div>
      </div>
    </>
  )
}
